import { AsyncPipe } from '@angular/common';
import { Component, OnInit, inject, input, output } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
  NgbDropdown,
  NgbDropdownToggle,
  NgbDropdownMenu,
  NgbDropdownButtonItem,
  NgbDropdownItem,
} from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import {
  FormlyButtonDropdown,
  FormlyButtonDropdownItem,
  GridPreviousNext,
  SummaryButton,
  SummaryItem,
} from '../../models';
import { MenuService, ModalService, RouterService, SystemEnvironmentService } from '../../services';
import { FormService } from '../../services/component-services/form.service';
import { DiscardChangesModalComponent } from '../modals/discard-changes-modal/discard-changes-modal.component';
import {
  SaveDiscardCancelChoice,
  SaveDiscardCancelModalComponent,
} from '../modals/save-discard-cancel-modal/save-discard-cancel-modal.component';
import { BaseComponent } from '../base/base.component';
import { BackButtonComponent } from '../back-button/back-button.component';
import { SummaryComponent } from '../summary/summary.component';
import { ignoreModalError } from '../../helpers';

@Component({
  selector: 'app-summary-top',
  templateUrl: './summary-top.component.html',
  styleUrl: './summary-top.component.scss',
  imports: [
    BackButtonComponent,
    SummaryComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownButtonItem,
    NgbDropdownItem,
    AsyncPipe,
  ],
})
export class SummaryTopComponent extends BaseComponent implements OnInit {
  protected readonly menuService = inject(MenuService);
  private readonly formService = inject(FormService);
  private readonly routerService = inject(RouterService);
  private readonly modalService = inject(ModalService);
  private readonly route = inject(ActivatedRoute);
  private readonly title = inject(Title);
  private readonly systemEnvironmentService = inject(SystemEnvironmentService);

  public readonly header = input<string>();
  public readonly name = input<string>();
  readonly model$ = input<Observable<any>>();
  public readonly additionalButtons = input<(SummaryButton | FormlyButtonDropdown)[]>([]);
  public readonly alwaysShowAdditionalButtons = input(false);
  public readonly summaryItems = input<(model: any) => SummaryItem[]>();
  public readonly fullWidth = input(false);
  public readonly mobile = input(false);
  public readonly showBack = input(true);
  public readonly showCopy = input(false);
  public readonly showSave = input(true);
  public readonly saveButtonText = input($localize`Save`);
  public readonly copyButtonText = input($localize`Copy`);
  public readonly enableCopyButton = input(true);
  public readonly overrideSave = input(false);
  // Previous / Next supplied by a MODAL host (a line editor opened from a grid of sibling lines).
  // Routed screens keep reading their ids from RouterService; a modal shows no arrows unless its
  // host passes this. Clicking emits the id to move to - after the unsaved-changes prompt below -
  // and the host swaps the record; nothing here navigates, because a modal has no route.
  public readonly previousNext = input<GridPreviousNext>();
  public readonly movePrevious = output<string>();
  public readonly moveNext = output<string>();
  public readonly copyModel = output<void>();
  public readonly saveClicked = output<void>();

  protected readonly createHeader = $localize`Create`;
  protected readonly editHeader = $localize`Edit`;

  private modalLevel: number;
  private saved = false;

  // Accidental double (or triple) clicks fire an action again before the first round
  // trip lands - a transfer pallet got the same roll loaded twice from exactly this.
  // Every ACTION button here (Save, Copy, additional buttons, dropdown items) shares
  // one short cooldown; clicks inside it are ignored. Deliberately time-based rather
  // than disable-until-complete: the click callbacks are fire-and-forget, so there is
  // no completion signal to re-enable on, and a cooldown can never dead-lock a button
  // after a failed save. Navigation (back/previous/next) is left unguarded.
  private static readonly ActionCooldownMs = 800;
  private lastActionAt = 0;

  protected guarded(action?: () => void) {
    const now = Date.now();
    if (now - this.lastActionAt < SummaryTopComponent.ActionCooldownMs) {
      return;
    }
    this.lastActionAt = now;
    action?.();
  }

  protected ids: GridPreviousNext = {} as GridPreviousNext;

  ngOnInit(): void {
    this.modalLevel = this.modalService.modalLevel;
    this.formService.saveComplete.pipe(takeUntil(this.destroy$)).subscribe(() => (this.saved = true));
    if (!this.isModal) {
      this.ids = this.routerService.previousNextIds();
    }

    if (this.header() || this.name()) {
      this.title.setTitle(this.systemEnvironmentService.pageTitle(this.header() ?? this.name()));
    }
  }

  get isModal() {
    return this.modalLevel > 0;
  }

  /** The arrows' ids: the host's, when it supplies them, else the routed screen's. */
  protected get navIds(): GridPreviousNext {
    return this.previousNext() ?? this.ids;
  }

  get showSaveButton() {
    return this.showSave() && !this.menuService.isReadOnly;
  }

  showAdditionalButtons(model: any) {
    return (
      !!this.additionalButtons()?.length &&
      !this.menuService.isReadOnly &&
      (this.alwaysShowAdditionalButtons() || !this.isNewModel(model))
    );
  }

  showCopyButton(model: any) {
    return this.showCopy() && !this.menuService.isReadOnly && !this.isNewModel(model);
  }

  isNewModel(model: any): boolean {
    return this.formService.isNewModel(model);
  }

  dropdownItems(button: SummaryButton | FormlyButtonDropdown): FormlyButtonDropdownItem[] {
    return this.isDropdown(button) ? button.items : [];
  }

  isDropdown(button: SummaryButton | FormlyButtonDropdown): button is FormlyButtonDropdown {
    return !!button && typeof button === 'object' && 'items' in button && Array.isArray((button as any).items);
  }

  dropdownLabel(button: SummaryButton | FormlyButtonDropdown): string {
    if (!this.isDropdown(button)) return '';
    return button.items?.[0]?.text ?? 'Actions';
  }

  dropdownIconClass(button: SummaryButton | FormlyButtonDropdown): string {
    if (!this.isDropdown(button)) return '';
    return button.items?.[0]?.iconClass ?? 'info';
  }

  dropdownMenuItems(button: SummaryButton | FormlyButtonDropdown): FormlyButtonDropdownItem[] {
    if (!this.isDropdown(button)) return [];
    return button.items?.slice(1) ?? [];
  }

  onDropdownItemClick(item: FormlyButtonDropdownItem, model: any) {
    this.guarded(() => {
      if (item.onClick) {
        item.onClick();
      } else if (item.select) {
        item.select(model);
      }
    });
  }

  copy() {
    this.guarded(() => this.copyModel.emit());
  }

  back() {
    if (this.formService.anyFormChanges(this.modalLevel)) {
      this.modalService
        .openMedium<DiscardChangesModalComponent>(DiscardChangesModalComponent)
        .modal.result.then((discardChanges: boolean) => {
          if (discardChanges) {
            this.formService.resetForms(this.modalService.modalLevel);
            this.navigate();
          }
        }, ignoreModalError);
    } else {
      this.navigate();
    }
  }

  previous() {
    this.moveTo(this.navIds.previousId, this.movePrevious);
  }

  next() {
    this.moveTo(this.navIds.nextId, this.moveNext);
  }

  save() {
    this.guarded(() => this.triggerSave());
  }

  private triggerSave() {
    this.saveClicked.emit();

    if (this.overrideSave()) {
      return;
    }

    this.formService.save.next(this.modalLevel);
  }

  // A routed screen navigates as it always has (the route's DiscardChangesGuard covers unsaved
  // changes). A modal host gets the id AFTER the user has settled any unsaved changes, because
  // no guard runs inside a modal.
  private moveTo(id: string, target: { emit(id: string): void }) {
    if (!this.previousNext()) {
      this.routerService.edit(id, this.route);
      return;
    }
    this.confirmMove().then((proceed: boolean) => proceed && target.emit(id), ignoreModalError);
  }

  private confirmMove(): Promise<boolean> {
    if (!this.formService.anyFormChanges(this.modalLevel)) {
      return Promise.resolve(true);
    }
    return this.modalService
      .openMedium<SaveDiscardCancelModalComponent>(SaveDiscardCancelModalComponent)
      .modal.result.then((choice: SaveDiscardCancelChoice) => {
        if (choice === 'discard') {
          this.formService.resetForms(this.modalLevel);
          return true;
        }
        return choice === 'save' ? this.saveThenContinue() : false;
      });
  }

  // "Save" on the prompt: save through the header's own path (so a host that overrides Save,
  // e.g. the allocations grid, still gets its own save) and continue once the save completes.
  // The form is told to keep the modal open for this one save. If nothing starts an HTTP save -
  // the form was invalid, or the host's override saves some other way - the move is abandoned
  // and the user is left where they were, with their edit; a failed save never fires
  // saveComplete, so it also stays put.
  private saveThenContinue(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.formService.saveComplete.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => resolve(true));
      this.formService.keepOpenAfterSave(this.modalLevel);
      this.triggerSave();
      if (!this.formService.saveInFlight) {
        this.formService.consumeKeepOpen(this.modalLevel);
        resolve(false);
      }
    });
  }

  private navigate() {
    if (this.isModal) {
      if (this.saved) {
        this.modalService.close();
      } else {
        this.modalService.dismiss();
      }
    } else if (this.mobile()) {
      this.routerService.top();
    } else {
      this.routerService.back(this.route);
    }
  }
}
